import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { appointmentsApi, type AppointmentQueryParams } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { CreateAppointmentRequest, UpdateAppointmentRequest, PaginationParams } from '@geely/shared';

interface UseAppointmentsOptions {
  enabled?: boolean;
}

export function useAppointments(params?: AppointmentQueryParams, options: UseAppointmentsOptions = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.appointments(params),
    queryFn: async () => {
      const response = await appointmentsApi.list(params);
      return response.data;
    },
    enabled,
  });
}

export function useAppointmentsPaginated(params: AppointmentQueryParams & Required<PaginationParams>) {
  return useQuery({
    queryKey: queryKeys.appointmentsPaginated(params),
    queryFn: async () => {
      const response = await appointmentsApi.listPaginated(params);
      return response.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useAppointment(id: string) {
  return useQuery({
    queryKey: queryKeys.appointment(id),
    queryFn: async () => {
      const response = await appointmentsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAppointmentRequest) => {
      const response = await appointmentsApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAppointmentRequest }) => {
      const response = await appointmentsApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await appointmentsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
